/**
 * Task-domain backend error codes.
 *
 * Must match `ErrorCodes` in be/utils/errors.js (Task Workflow group).
 *
 * Codes are the stable API contract; the backend's `error` string is Arabic
 * and exists for legacy clients and logs. Translation lives in the locale
 * files (`tasks.*` keys) — these are just the identifiers, so a code maps to
 * whatever language the reader uses.
 *
 * Client rule: if a response carries `code`, translate it; otherwise fall back
 * to displaying the server's message. Never infer meaning from message text.
 */
export const TASK_ERROR_CODES = {
  // Rejected state transition: clean can't close with unanswered items
  CHECKLIST_INCOMPLETE: 'TASK_001',
  // Turnover clean closed without "would you host this guest again?" (only while the switch is ON)
  GUEST_ASSESSMENT_REQUIRED: 'TASK_002',
  // The answer is incomplete — `details.field` names the part: answer | reasonCode | note | photo
  GUEST_ASSESSMENT_INVALID: 'TASK_003',
  // Photo delete refused: the last `issue` photo behind a recorded «لا» is that answer's evidence
  GUEST_ASSESSMENT_EVIDENCE: 'TASK_004',
  // No such task — or one this reader may not know exists (a complaint repair task, for an owner)
  NOT_FOUND: 'TASK_005',
  // A create request the server cannot honour as described — `details.field` names the part:
  // type | property | title | scheduledDate | priority | cleaningMode | assignedTo | restockItems | notes
  INVALID: 'TASK_006',
  // The task is finished with, so the action has nothing to act on — chasing a closed task
  NOT_ACTIONABLE: 'TASK_007',
  // «انضممت» refused: the clean already has as many people as its unit size allows (details.limit)
  CREW_FULL: 'TASK_008',
  // «انضممت» refused: a clean is joined only while it is under way
  JOIN_NOT_OPEN: 'TASK_009',
  // Undoing a start is a supervisor's correction, not a crew action
  START_UNDO_FORBIDDEN: 'TASK_010'
};

// The words for each part of the guest question that can be missing — for a problem the screen finds
// before sending AND for the server's TASK_002 / TASK_003 (`details.field`). One map, both uses.
export const GUEST_ASSESSMENT_MESSAGE_KEYS = {
  required: 'tasks.guestAssessment.errorRequired',
  answer: 'tasks.guestAssessment.errorAnswer',
  reasonCode: 'tasks.guestAssessment.errorReason',
  note: 'tasks.guestAssessment.errorNote',
  photo: 'tasks.guestAssessment.errorPhoto'
};

/**
 * A refused task action (a close, a photo delete) as a translation key — from the server's `code`,
 * never its Arabic text. `null` when the response carries no code we know: the caller then shows
 * the server's message.
 */
/**
 * The part of a create request the server refused (`TASK_006` → `details.field`), as a translation
 * key. One key per field, so a form can say which box is wrong instead of a general apology.
 */
export const TASK_INVALID_FIELD_KEYS = {
  type: 'tasks.create.invalidType',
  property: 'tasks.create.invalidProperty',
  title: 'tasks.create.invalidTitle',
  scheduledDate: 'tasks.create.invalidScheduledDate',
  priority: 'tasks.create.invalidPriority',
  cleaningMode: 'tasks.create.invalidCleaningMode',
  assignedTo: 'tasks.create.invalidAssignedTo',
  restockItems: 'tasks.create.invalidRestockItems',
  notes: 'tasks.create.invalidNotes',
  // A supervisor's correction to a clean's record (PATCH /tasks/:id/clean-record)
  reason: 'tasks.cleanRecord.invalidReason',
  change: 'tasks.cleanRecord.invalidChange',
  status: 'tasks.cleanRecord.invalidStatus',
  startedAt: 'tasks.cleanRecord.invalidStartedAt',
  cleanerUserIds: 'tasks.cleanRecord.invalidCleaners'
};

export const taskRefusalKey = (data) => {
  switch (data?.code) {
    case TASK_ERROR_CODES.CHECKLIST_INCOMPLETE:
      return 'tasks.card.completeBlockedMessage';
    case TASK_ERROR_CODES.GUEST_ASSESSMENT_REQUIRED:
      return GUEST_ASSESSMENT_MESSAGE_KEYS.required;
    case TASK_ERROR_CODES.GUEST_ASSESSMENT_INVALID:
      return GUEST_ASSESSMENT_MESSAGE_KEYS[data?.details?.field] || GUEST_ASSESSMENT_MESSAGE_KEYS.answer;
    case TASK_ERROR_CODES.GUEST_ASSESSMENT_EVIDENCE:
      return 'tasks.guestAssessment.errorPhotoLocked';
    case TASK_ERROR_CODES.NOT_ACTIONABLE:
      return 'tasks.attention.notActionable';
    case TASK_ERROR_CODES.CREW_FULL:
      return 'tasks.card.joinCrewFull';
    case TASK_ERROR_CODES.JOIN_NOT_OPEN:
      return 'tasks.card.joinNotOpen';
    case TASK_ERROR_CODES.START_UNDO_FORBIDDEN:
      return 'tasks.card.undoStartForbidden';
    case TASK_ERROR_CODES.INVALID:
      // A field we do not know yet (a newer server) falls back to the general line rather than
      // rendering a raw key at somebody.
      return TASK_INVALID_FIELD_KEYS[data?.details?.field] || 'tasks.create.error';
    default:
      return null;
  }
};

export default TASK_ERROR_CODES;
